import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import BarChartIcon from '@material-ui/icons/BarChartSharp';
import TableChartIcon from '@material-ui/icons/TableChartSharp';

export const TOGGLE_VIEW_OPTIONS = {
  CHART: 'chart',
  TABLE: 'table'
};

export const DEFAULT_VIEW = TOGGLE_VIEW_OPTIONS.CHART;

const useStyles = makeStyles(theme => ({
  toggleButtonGroup: {
    marginLeft: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  toggleButton: {
    height: theme.spacing(4),
    padding: theme.spacing(0.5, 1),
    textTransform: 'none',
    color: theme.palette.text.secondary,
    border: 'none',
    borderRadius: theme.shape.borderRadius
  },
  icon: {
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.secondary
  }
}));

const getAriaLabels = formatMessage => ({
  viewTypeSelection: formatMessage({
    id:
      'resourceManagementDashboard.benchOverAllocationTracker.viewTypeSelection'
  }),
  chartView: formatMessage({
    id:
      'resourceManagementDashboard.benchOverAllocationTracker.chartViewAriaLabel'
  }),
  tableView: formatMessage({
    id:
      'resourceManagementDashboard.benchOverAllocationTracker.tableViewAriaLabel'
  })
});

const BenchOverAllocationDetailsViewToggleButton = ({
  selectedView,
  onViewChange
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const viewToggleLabels = getAriaLabels(formatMessage);

  const handleViewChange = useCallback(
    (_, newView) => {
      if (newView !== null) {
        onViewChange(newView);
      }
    },
    [onViewChange]
  );

  return (
    <ToggleButtonGroup
      exclusive
      value={selectedView}
      onChange={handleViewChange}
      className={classes.toggleButtonGroup}
      aria-label={viewToggleLabels.viewTypeSelection}
    >
      <ToggleButton
        value={TOGGLE_VIEW_OPTIONS.CHART}
        className={classes.toggleButton}
        aria-label={viewToggleLabels.chartView}
      >
        <BarChartIcon className={classes.icon} fontSize="small" />
        <Typography variant="body2" component="span">
          <FormattedMessage id="resourceManagementDashboard.benchOverAllocationTracker.chartView" />
        </Typography>
      </ToggleButton>
      <ToggleButton
        value={TOGGLE_VIEW_OPTIONS.TABLE}
        className={classes.toggleButton}
        aria-label={viewToggleLabels.tableView}
      >
        <TableChartIcon className={classes.icon} fontSize="small" />
        <Typography variant="body2" component="span">
          <FormattedMessage id="resourceManagementDashboard.benchOverAllocationTracker.tableView" />
        </Typography>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

BenchOverAllocationDetailsViewToggleButton.propTypes = {
  selectedView: PropTypes.oneOf(Object.values(TOGGLE_VIEW_OPTIONS)).isRequired,
  onViewChange: PropTypes.func.isRequired
};

export default BenchOverAllocationDetailsViewToggleButton;
